import { FreelancerSearchResult } from '../../types/search';
import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import FreelancerProfileModalContent from './FreelancerProfileModalContent';
import { useState } from 'react';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import ReportModal from '../reports/ReportModal';
import { useProfileRole } from '../../hooks/useProfileRole';
import ProfileBioText from '../common/ProfileBioText';

interface FreelancerProfileModalProps {
  freelancer: FreelancerSearchResult;
  onClose: () => void;
}

export default function FreelancerProfileModal({
  freelancer,
  onClose,
}: FreelancerProfileModalProps) {
  const { t } = useTranslation(['freelancers', 'common', 'reports']);

  const { isAdmin } = useProfileRole();

  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[72rem]">
        <div className="flex flex-row items-center">
          <div className="absolute top-2.5 right-1 text-gray-400 hover:text-gray-600 text-2xl  md:right-8">
            <ReportButtonMenu
              onReport={() => setShowReportModal(true)}
              entityOwner={freelancer.id}
              onCopySuccess={handleCopySuccess}
              contentTypeTitle="report_this_user"
            />
          </div>

          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
            onClick={onClose}
            aria-label={t('common:form_actions.close')}
          >
            &times;
          </button>
        </div>

        <FreelancerProfileModalContent freelancer={freelancer} className="pt-6 md:pt-0" />
        <hr className="my-4 border-[#E1E9F0]" />

        {freelancer.bio && (
          <div className="mb-4">
            <div className="font-bold text-lg mb-1 text-secondary">
              {t('about', { ns: 'freelancers', defaultValue: 'À propos' })}
            </div>
            <div className="text-secondary mb-8">
              <ProfileBioText bio={freelancer.bio} profileId={freelancer.id} />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button
            className="w-full md:w-auto md:px-8"
            variant="primary"
            href={isAdmin ? `/admin/freelancers/${freelancer.id}` : `/freelancers/${freelancer.id}`}
            onClick={onClose}
          >
            {t('viewFullProfile', { ns: 'freelancers', defaultValue: 'Voir le profil complet' })}
          </Button>

          <button
            className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
            onClick={onClose}
          >
            {t('common:form_actions.close')}
          </button>
        </div>
      </FullBlockingModal>

      {showReportModal && freelancer && (
        <ReportModal
          entityId={freelancer.id}
          reportedId={freelancer.id}
          contentType="profile"
          onClose={() => setShowReportModal(false)}
        />
      )}
      {copied && <BottomSuccessMessage />}
    </>
  );
}
